/**
 * Подвал сайта.
 *
 * Устройство взято у polymarket.com: логотип с одной строкой о продукте,
 * несколько колонок ссылок с подписями под каждой, снизу — юридическая
 * строка и дисклеймер. Приём работает потому, что подвал перестаёт быть
 * свалкой ссылок: каждая колонка отвечает на вопрос одного человека —
 * участника, бренда, проверяющего.
 *
 * Соцсети стоят рядом с логотипом, а не отдельной колонкой: это не раздел
 * продукта, а способ связи. Значки монохромные — чужие фирменные цвета
 * превратили бы подвал в радугу.
 */
import { Logo, SocialIcon } from '@allswipes/ui';
import { Link } from 'react-router-dom';

import { useLang } from '@allswipes/i18n';

import { SOCIALS, sitePage } from '../site';

interface FooterLink {
  label: string;
  note: string;
  to?: string;
  href?: string;
}

const SPHERES: FooterLink[] = [
  { label: 'Магазины', note: 'Акции и шансы', to: '/?sphere=grocery' },
  { label: 'Напитки', note: 'Акции и шансы', to: '/?sphere=drinks' },
  { label: 'Продукты', note: 'Акции и шансы', to: '/?sphere=food' },
  { label: 'Кафе', note: 'Акции и шансы', to: '/?sphere=cafe' },
  { label: 'Аптеки', note: 'Акции и шансы', to: '/?sphere=pharmacy' },
  { label: 'Электроника', note: 'Акции и шансы', to: '/?sphere=electronics' },
  { label: 'Заправки', note: 'Акции и шансы', to: '/?sphere=fuel' },
  { label: 'Все сферы', note: 'Вся лента целиком', to: '/' },
];

const FOR_PEOPLE: FooterLink[] = [
  { label: 'Мои участия', note: 'Билеты и призы', to: '/my' },
  { label: 'Архив акций', note: 'Что уже прошло', to: '/archive' },
  { label: 'Баллы и ранги', note: 'За что начисляют', to: '/points' },
  { label: 'Сканировать код', note: 'С упаковки или чека', to: '/scan' },
  { label: 'Анкета и согласия', note: 'Кому переданы данные', to: '/profile' },
];

const TRUST: FooterLink[] = [
  { label: 'Как это проверяется', note: 'Провабли-фейр', href: '/about#fair' },
  { label: 'Политика данных', note: 'Что хранится и зачем', href: '/privacy' },
  { label: 'Условия', note: 'Кто за что отвечает', href: '/terms' },
];

const CONSOLE_URL = import.meta.env.VITE_CONSOLE_URL ?? 'http://localhost:5174';

const BRAND: FooterLink[] = [
  { label: 'О проекте', note: 'Что это и кому', href: '/about' },
  { label: 'Кабинет партнёра', note: 'Вход для брендов', href: CONSOLE_URL },
  { label: 'Пилотный статус', note: 'Чего проект не обещает', href: '/terms' },
];

export function SiteFooter() {
  const { lang, t } = useLang();
  return (
    <footer className="sfoot">
      <div className="sfin">
        <div className="sfbrand">
          <Logo height={18} />
        </div>
        <p className="sftag">{t('Платформа прозрачных промо-акций')}</p>

        <div className="sfsoc">
          {SOCIALS.map((s) => (
            <a
              key={s.name}
              href={s.href}
              title={s.label}
              aria-label={s.label}
              target={s.href.startsWith('mailto:') ? undefined : '_blank'}
              rel="noreferrer"
            >
              <SocialIcon name={s.name} size={17} />
            </a>
          ))}
        </div>

        <div className="sfcols">
          <Column title={t('Акции по сферам')} links={SPHERES} wide />
          <Column title={t('Участнику')} links={FOR_PEOPLE} />
          <Column title={t('Проверка')} links={TRUST} />
          <Column title="ALLSWIPES" links={BRAND} />
        </div>

        <div className="sfbot">
          <span>© {new Date().getFullYear()} ALLSWIPES</span>
          <span className="dot">·</span>
          <a href={sitePage('/terms', lang)}>{t('Условия использования')}</a>
          <span className="dot">·</span>
          <a href={sitePage('/privacy', lang)}>{t('Политика конфиденциальности')}</a>
          <span className="dot">·</span>
          <Link to="/profile">{t('Мои согласия')}</Link>
        </div>

        <p className="sfnote">
          {t(
            'Акции носят рекламный характер и не являются лотереей. Организатором каждой акции выступает бренд, указанный в её правилах; ALLSWIPES обеспечивает проведение и проверяемость результата. Условия конкретной акции — в её правилах, подписанных хешем, и они не меняются после запуска.',
          )}
        </p>
      </div>
    </footer>
  );
}

function Column({ title, links, wide }: { title: string; links: FooterLink[]; wide?: boolean }) {
  const { lang, t } = useLang();
  return (
    <div className={`sfcol ${wide ? 'wide' : ''}`}>
      <div className="sfh">{title}</div>
      <div className="sfl">
        {links.map((link) =>
          link.to ? (
            <Link key={link.label} to={link.to}>
              <span className="t">{t(link.label)}</span>
              <span className="n">{t(link.note)}</span>
            </Link>
          ) : (
            <a
              key={link.label}
              // Относительный путь — это серверная страница платформы, и язык
              // интерфейса надо донести до неё; абсолютный адрес (кабинет)
              // оставляем как есть.
              href={link.href?.startsWith('/') ? sitePage(link.href, lang) : link.href}
              target="_blank"
              rel="noreferrer"
            >
              <span className="t">{t(link.label)}</span>
              <span className="n">{t(link.note)}</span>
            </a>
          ),
        )}
      </div>
    </div>
  );
}
